/**
 * entities/organization/model/org-store — Active-org Zustand store.
 *
 * Holds the active organization ID and the cached org object.
 * The active org ID is persisted to localStorage (it is not a secret;
 * just a preference, like "last selected workspace").
 *
 * Public API (stable; consumed by dev-2-org-settings and other features):
 *
 *   State:
 *     activeOrgId  — string | null  (UUID of the active org, or null)
 *     activeOrg    — Organization | null  (cached full org object)
 *     orgs         — Organization[]  (list of all user's orgs; populated by OrgContextProvider)
 *
 *   Actions:
 *     setActiveOrg(org)     — set active org in the store (does NOT call the backend)
 *     setActiveOrgId(id)    — set active org ID only (used when clearing)
 *     setOrgs(orgs)         — update the list of organizations
 *     clearActiveOrg()      — clear active org (on logout)
 *
 * NOTE: The backend validation call (POST /api/organizations/set-active/) is
 * performed in the OrgContextProvider hook `useSetActiveOrg()`, not here.
 * This store is pure state — no side effects.
 *
 * The X-Org-ID header injection happens in shared/api/client.ts via the
 * `getActiveOrgId` accessor registered by this module (same pattern as token).
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Organization } from './types';
import { setActiveOrgIdAccessor } from '@/shared/api/middleware';
import { setActiveRoleAccessor } from '@/shared/auth/permissions';

// ---------------------------------------------------------------------------
// Store shape
// ---------------------------------------------------------------------------

export interface OrgStoreState {
  // --- State ---
  /** UUID of the currently active organization, or null when none selected. */
  activeOrgId: string | null;
  /** Cached full org object for the active org, or null. */
  activeOrg: Organization | null;
  /** All organizations the user belongs to. */
  orgs: Organization[];

  // --- Actions ---
  /** Set the active org (both id and cached object). */
  setActiveOrg: (org: Organization) => void;
  /** Set only the active org ID (useful when clearing or restoring). */
  setActiveOrgId: (id: string | null) => void;
  /** Replace the full list of organizations. */
  setOrgs: (orgs: Organization[]) => void;
  /** Clear the active org on logout or when the module is disabled. */
  clearActiveOrg: () => void;
}

// ---------------------------------------------------------------------------
// Store implementation
// ---------------------------------------------------------------------------

export const useOrgStore = create<OrgStoreState>()(
  persist(
    (set) => ({
      // --- Initial state ---
      activeOrgId: null,
      activeOrg: null,
      orgs: [],

      // --- Actions ---

      setActiveOrg(org: Organization) {
        set({ activeOrgId: org.id, activeOrg: org });
      },

      setActiveOrgId(id: string | null) {
        set({ activeOrgId: id });
      },

      setOrgs(orgs: Organization[]) {
        set({ orgs });
      },

      clearActiveOrg() {
        set({ activeOrgId: null, activeOrg: null, orgs: [] });
      },
    }),
    {
      name: 'active-org',
      // Only persist the activeOrgId — the full org object is re-fetched on mount.
      // This avoids serving stale org data across sessions.
      partialize: (state) => ({ activeOrgId: state.activeOrgId }),
    },
  ),
);

// ---------------------------------------------------------------------------
// Active-org ID accessor — injected into the API client middleware.
//
// Mirrors the pattern of shared/auth/token.ts: an injectable accessor so the
// middleware reads the live store value without importing the store directly.
// ---------------------------------------------------------------------------

/** Signature for a function that returns the current active org ID. */
export type ActiveOrgAccessor = () => string | null | undefined;

/** No-op stub — returns null until the provider registers a real accessor. */
const noopAccessor: ActiveOrgAccessor = () => null;

let _activeOrgAccessor: ActiveOrgAccessor = noopAccessor;

/**
 * Register the active-org accessor.
 * Called once at module evaluation time (bottom of this file) to wire the
 * Zustand store into the API client middleware.
 */
export function setActiveOrgAccessor(accessor: ActiveOrgAccessor): void {
  _activeOrgAccessor = accessor;
}

/** Get the current active org ID by delegating to the registered accessor. */
export function getActiveOrgId(): string | null | undefined {
  return _activeOrgAccessor();
}

/**
 * Reset to the no-op stub.
 * Used in tests to isolate accessor state between test cases.
 */
export function resetActiveOrgAccessor(): void {
  _activeOrgAccessor = noopAccessor;
}

// ---------------------------------------------------------------------------
// Wire the store into the accessor seams.
// Evaluated once when this module is imported.
// ---------------------------------------------------------------------------

/** Reads the live active org ID from the Zustand store. */
function getOrgIdFromStore(): string | null {
  return useOrgStore.getState().activeOrgId;
}

// Wire into the local (entity-layer) accessor.
setActiveOrgAccessor(getOrgIdFromStore);

// Wire into the shared/api/middleware accessor so the API client injects
// the X-Org-ID header on every request.
setActiveOrgIdAccessor(getOrgIdFromStore);

// Wire the current user's membership role into the shared/auth/permissions accessor
// so `useHasPerm` and `roleCan` can read the live role without shared importing entities.
setActiveRoleAccessor(() => useOrgStore.getState().activeOrg?.my_role ?? null);
